'use client';

import { c as _c } from "react/compiler-runtime";
import './tax-receipts-list.scss';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ItemCardSkeleton } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { LinkCard, LinkCardIcon } from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { TaxReceipt } from '@sola/schema/dotcom/proxy-api/taxReceipts.ts';
import { useTaxReceipts } from '@sola/supporter-sdk/hooks/tax-receipts.ts';
import { TaxReceiptCard } from '../../molecules/tax-receipt-card/tax-receipt-card.tsx';

/**
 * Get component data
 */
export const useComponentData = () => {
  const $ = _c(9);
  const {
    data,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useTaxReceipts();
  const taxReceiptsResult = data;
  let t0;
  if ($[0] !== taxReceiptsResult) {
    t0 = taxReceiptsResult?.filter(_temp).sort(_temp2) || [];
    $[0] = taxReceiptsResult;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const taxReceipts = t0;
  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const hasData = !!taxReceipts?.length;
  const hasNoReceipts = !isShowingSkeletons && !hasData && !isError;
  const hasReceipts = !isShowingSkeletons && hasData;
  const hasErrorAlert = !isShowingSkeletons && isError;
  const hasFootnote = !isShowingSkeletons && !isError;
  let t1;
  if ($[2] !== hasErrorAlert || $[3] !== hasFootnote || $[4] !== hasNoReceipts || $[5] !== hasReceipts || $[6] !== isShowingSkeletons || $[7] !== taxReceipts) {
    t1 = {
      isShowingSkeletons,
      hasNoReceipts,
      hasReceipts,
      hasErrorAlert,
      hasFootnote,
      taxReceipts
    };
    $[2] = hasErrorAlert;
    $[3] = hasFootnote;
    $[4] = hasNoReceipts;
    $[5] = hasReceipts;
    $[6] = isShowingSkeletons;
    $[7] = taxReceipts;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};

/**
 * Displays list of commitments based on frequency.
 */
export const TaxReceiptsList = () => {
  const $ = _c(20);
  const {
    t
  } = useTranslation("my");
  const {
    isShowingSkeletons,
    hasErrorAlert,
    hasFootnote,
    hasNoReceipts,
    hasReceipts,
    taxReceipts
  } = useComponentData();
  let t0;
  if ($[0] !== isShowingSkeletons) {
    t0 = isShowingSkeletons && <><ItemCardSkeleton /><ItemCardSkeleton /><ItemCardSkeleton /></>;
    $[0] = isShowingSkeletons;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hasReceipts || $[3] !== taxReceipts) {
    t1 = hasReceipts && <div className="flex flex-col gap-comfy-xs">{taxReceipts.map(_temp3)}</div>;
    $[2] = hasReceipts;
    $[3] = taxReceipts;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== hasNoReceipts || $[6] !== t) {
    t2 = hasNoReceipts && <Alert message={t("taxReceipts.noTaxReceipts")} />;
    $[5] = hasNoReceipts;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== hasErrorAlert || $[9] !== t) {
    t3 = hasErrorAlert && <Alert title={t("taxReceipts.requestError.title")} message={t("taxReceipts.requestError.message")} variant="negative" action={<Link href={t("ctas.contactSupport.url")}>{t("ctas.contactSupport.text")}</Link>} />;
    $[8] = hasErrorAlert;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hasFootnote || $[12] !== t) {
    t4 = hasFootnote && <><p className="label-moderate label-md body-neutral-mild">{t("taxReceipts.footnote")}</p><LinkCard graphic={<LinkCardIcon><ArkIcon size="18" iconName="message-question-square" /></LinkCardIcon>} title={t("taxReceipts.support.title")} subtitle={t("taxReceipts.support.subtitle")} href={t("ctas.contactSupport.url")} staticPadding={true} /></>;
    $[11] = hasFootnote;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t0 || $[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <div className="max-w-md flex flex-col gap-comfy-xl">{t0}{t1}{t2}{t3}{t4}</div>;
    $[14] = t0;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
};
function _temp(taxReceipt) {
  return taxReceipt.url && taxReceipt.year;
}
function _temp2(a, b) {
  return b.year - a.year;
}
function _temp3(taxReceipt) {
  return <TaxReceiptCard key={`tax-receipt-${taxReceipt.year}`} taxReceipt={taxReceipt} />;
}